import apiClient from 'services/api';

/**
 * A versão do app nativo diante do que o backend exige (GET /app/versao) e do que a Play Store oferece.
 *
 * O app leva o front embutido e só muda com publicação nova na Play Store; quem desliga a atualização
 * automática fica para trás sem saber. Toda atualização é obrigatória: abaixo da versão publicada, o app
 * bloqueia até atualizar. O backend guarda os números nas Configurações Gerais, em versionCode (o inteiro
 * que cresce a cada build, android/app/build.gradle):
 *   - `recomendada`: na tela, "Versão publicada". É a que bloqueia hoje;
 *   - `minima`: reservada para uso futuro (sai 0). Abaixo dela também bloqueia, para já valer quando voltar.
 * 0 desliga. Ver docs/atualizacao-do-app.md e prompts/verificacao-de-versao-do-app.md.
 *
 * Só bloqueia quando a Play Store DESTE aparelho já oferece a versão exigida: depois do deploy a loja
 * leva cerca de uma hora para mostrar a atualização (e o lançamento pode ser gradual), e um bloqueio
 * antes disso mandaria a pessoa para uma loja sem nada para instalar. Enquanto isso, 'aguardando-loja'.
 *
 * Toda falha (sem rede, endpoint fora, versão ilegível, Play sem resposta) não bloqueia: uma trava que
 * derruba o app quando algo oscila é pior que nenhuma.
 */

export const BLOQUEADA = 'bloqueada';
export const AGUARDANDO_LOJA = 'aguardando-loja';
export const ATUAL = 'atual';

const inteiroPositivo = (valor) => {
	const numero = Number(valor);
	return Number.isInteger(numero) && numero > 0 ? numero : 0;
};

/** O versionCode que o backend exige, ou 0. */
export const versaoExigida = ({ minima, recomendada } = {}) =>
	Math.max(inteiroPositivo(minima), inteiroPositivo(recomendada));

/**
 * 'bloqueada' | 'aguardando-loja' | 'atual'.
 *
 * @param {object} p
 * @param {number|string} p.atual         versionCode instalado
 * @param {number} p.minima
 * @param {number} p.recomendada
 * @param {number|string|null} p.naLoja   versionCode que a Play oferece a este aparelho, ou null
 */
export function situacaoDaVersao({ atual, minima, recomendada, naLoja } = {}) {
	const versao = inteiroPositivo(atual);
	const exigida = versaoExigida({ minima, recomendada });
	if (!versao || !exigida || versao >= exigida) return ATUAL;
	return inteiroPositivo(naLoja) >= exigida ? BLOQUEADA : AGUARDANDO_LOJA;
}

/** `{ minima, recomendada }` do Android, ou null se não deu para saber. */
export async function buscarVersaoExigida() {
	try {
		const { data } = await apiClient.get('/app/versao');
		return data?.android ?? null;
	} catch {
		return null;
	}
}

/**
 * O versionCode que a Play Store oferece a este aparelho, ou null: sem atualização, app instalado fora
 * da loja (por cabo, no desenvolvimento), aparelho sem Google Play, ou qualquer erro. "Em andamento"
 * conta como oferecida: a pessoa já começou a atualizar.
 */
export async function versaoNaLoja() {
	try {
		const { AppUpdate, AppUpdateAvailability } = await import('@capawesome/capacitor-app-update');
		const info = await AppUpdate.getAppUpdateInfo();
		const oferecida = info?.updateAvailability === AppUpdateAvailability.UPDATE_AVAILABLE
			|| info?.updateAvailability === AppUpdateAvailability.UPDATE_IN_PROGRESS;
		return oferecida ? inteiroPositivo(info.availableVersionCode) || null : null;
	} catch {
		return null;
	}
}
